/**
 * Espelho dos templates de profissão pra UI.
 * Mantido em sincronia com backend/functions/src/lib/professionTemplates.ts
 *
 * O frontend usa isso pra:
 *  - Mostrar a galeria de templates ("escolha seu perfil")
 *  - Saber qual ícone/cor exibir
 *  - Fallback se o tenant ainda não aplicou nenhum template
 */

export type ProfessionId =
  | 'cerimonial' | 'dj' | 'decorador' | 'buffet' | 'salao' | 'eventos_completo'

export interface VocabMap {
  events: string
  event_singular: string
  event_action: string
  clients: string
  client_singular: string
  team: string
  suppliers: string
  proposals: string
  contracts: string
  schedule: string
}

export interface ProfessionTemplateMeta {
  id: ProfessionId
  nome: string
  descricao: string
  icone: string
  default_color: string
  modules: string[]
  vocab: VocabMap
}

// Vocabulário padrão (usado quando tenant não tem nenhum perfil)
export const DEFAULT_VOCAB: VocabMap = {
  events: 'Eventos',
  event_singular: 'Evento',
  event_action: 'Novo evento',
  clients: 'Clientes',
  client_singular: 'Cliente',
  team: 'Equipe',
  suppliers: 'Fornecedores',
  proposals: 'Propostas',
  contracts: 'Contratos',
  schedule: 'Cronograma',
}

export const PROFESSION_TEMPLATES: Record<ProfessionId, ProfessionTemplateMeta> = {
  cerimonial: {
    id: 'cerimonial',
    nome: 'Cerimonial',
    descricao: 'Cerimonialistas e assessores. Foco em roteiro e contratos.',
    icone: '💍',
    default_color: '#D4A857',
    modules: ['dashboard', 'clients', 'events', 'proposals', 'contracts', 'finance', 'schedule', 'reports', 'support', 'settings'],
    vocab: {
      events: 'Cerimônias', event_singular: 'Cerimônia', event_action: 'Nova cerimônia',
      clients: 'Noivos & Famílias', client_singular: 'Cliente',
      team: 'Assessoras', suppliers: 'Parceiros',
      proposals: 'Orçamentos', contracts: 'Contratos', schedule: 'Roteiro',
    },
  },
  dj: {
    id: 'dj',
    nome: 'DJ',
    descricao: 'DJs profissionais. CRM, equipamentos, briefing musical, agenda e financeiro num lugar só.',
    icone: '🎧',
    default_color: '#A78BFA',
    modules: [
      'dashboard',
      'clients',           // CRM
      'leads',             // funil de leads (Instagram, indicação, site)
      'events',            // eventos contratados
      'schedule',          // cronograma do dia
      'proposals',         // orçamentos com pacotes pré-cadastrados
      'contracts',         // contratos digitais
      'finance',           // receber/pagar + lucro real
      'suppliers',         // locadoras de equipamento
      'team',              // técnicos, ajudantes, VJs
      'equipamentos',      // ★ novo: patrimônio (Onda 4)
      'briefing_musical',  // ★ novo: aparece dentro do evento (Onda 3)
      'whatsapp',          // canal direto com cliente
      'reports',           // KPIs DJ (Onda 5)
      'support',
      'settings',
    ],
    vocab: {
      // Vocabulário neutro pra DJ que faz casamento, formatura e corporativo.
      // "Shows" estava soando "banda" e confundia o cliente.
      events: 'Eventos', event_singular: 'Evento', event_action: 'Novo evento',
      clients: 'Clientes', client_singular: 'Cliente',
      team: 'Equipe técnica', suppliers: 'Fornecedores & locadoras',
      proposals: 'Orçamentos', contracts: 'Contratos',
      schedule: 'Cronograma do evento',
    },
  },
  decorador: {
    id: 'decorador',
    nome: 'Decorador',
    descricao: 'Decoradores e ambientadores. Foco em paleta e materiais.',
    icone: '🌸',
    default_color: '#F472B6',
    modules: ['dashboard', 'clients', 'events', 'proposals', 'contracts', 'suppliers', 'team', 'finance', 'reports', 'support', 'settings'],
    vocab: {
      events: 'Projetos', event_singular: 'Projeto', event_action: 'Novo projeto',
      clients: 'Clientes', client_singular: 'Cliente',
      team: 'Montadores', suppliers: 'Fornecedores',
      proposals: 'Orçamentos', contracts: 'Contratos', schedule: 'Cronograma de montagem',
    },
  },
  buffet: {
    id: 'buffet',
    nome: 'Buffet',
    descricao: 'Buffets e gastronomia de eventos. Foco em cardápio e brigada.',
    icone: '🍽️',
    default_color: '#E8B968',
    modules: ['dashboard', 'clients', 'events', 'proposals', 'contracts', 'team', 'suppliers', 'finance', 'reports', 'support', 'settings'],
    vocab: {
      events: 'Eventos', event_singular: 'Evento', event_action: 'Novo evento',
      clients: 'Anfitriões', client_singular: 'Anfitrião',
      team: 'Brigada', suppliers: 'Fornecedores',
      proposals: 'Orçamentos', contracts: 'Contratos', schedule: 'Cronograma de cozinha',
    },
  },
  salao: {
    id: 'salao',
    nome: 'Salão (locação)',
    descricao: 'Salões e espaços que só locam o ambiente.',
    icone: '🏛️',
    default_color: '#58A6FF',
    modules: ['dashboard', 'clients', 'events', 'proposals', 'contracts', 'finance', 'reports', 'support', 'settings'],
    vocab: {
      events: 'Locações', event_singular: 'Locação', event_action: 'Nova locação',
      clients: 'Locatários', client_singular: 'Locatário',
      team: 'Equipe', suppliers: 'Parceiros recomendados',
      proposals: 'Cotações', contracts: 'Contratos de locação', schedule: 'Agenda do espaço',
    },
  },
  eventos_completo: {
    id: 'eventos_completo',
    nome: 'Eventos Completo',
    descricao: 'Cerimonial + buffet + decoração + equipe + fornecedores.',
    icone: '🎊',
    default_color: '#D4A857',
    modules: ['dashboard', 'clients', 'events', 'proposals', 'contracts', 'finance', 'suppliers', 'team', 'reports', 'schedule', 'whatsapp', 'leads', 'support', 'settings'],
    vocab: DEFAULT_VOCAB,
  },
}

export const TEMPLATE_LIST = Object.values(PROFESSION_TEMPLATES)

export function getTemplate(id: ProfessionId | string | undefined): ProfessionTemplateMeta {
  if (id && id in PROFESSION_TEMPLATES) {
    return PROFESSION_TEMPLATES[id as ProfessionId]
  }
  return PROFESSION_TEMPLATES.eventos_completo
}
